import { ChevronDown } from "lucide-react";
import { motion } from "motion/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { useFieldOptionsFetcher } from "../api/useFieldOptionsFetcher";
import { VALUE_INPUT } from "../conditions";
import { MOTION_EASE } from "../constants";
import { useConditionEditor } from "../hooks/useConditionEditor";
import { getOperatorLabel } from "../operatorLabels";
import ValueInput from "./ValueInput";

import { AsyncSelect } from "@/components/ui/v2/async-select";
import { Button } from "@/components/ui/v2/button";
import { Label } from "@/components/ui/v2/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/v2/select";
import { Switch } from "@/components/ui/v2/switch";
import {
  SEGMENT_TOUR_TARGET,
  TOUR_DEMO_CONTROL,
  type TourDemoControl,
} from "@/features/segments/tour/constants";
import type { SegmentField } from "@/features/segments/types";
import { useTourTarget } from "@/features/tours/useTourTarget";
import { toSentenceCase } from "@/lib/helpers/common";

type ConditionEditorProps = {
  id: string;
  ruleIndex: number;
  conditionIndex: number;
  fields: SegmentField[];
  clientId?: string;
  tourDemoControl: TourDemoControl | null;
  onRemove: () => void;
};

const REVEAL_TRANSITION = { duration: 0.15, ease: MOTION_EASE } as const;

type FieldErrorProps = { id: string; message?: string };

function FieldError({ id, message }: FieldErrorProps) {
  if (!message) return null;
  return (
    <p id={id} className="text-sm text-destructive">
      {message}
    </p>
  );
}

const getErrorId = (editorId: string, name: string) =>
  `${editorId}-${name}-error`;
const getDescribedBy = (editorId: string, name: string, message?: string) =>
  message ? getErrorId(editorId, name) : undefined;

export function ConditionEditor({
  id,
  ruleIndex,
  conditionIndex,
  fields,
  clientId,
  tourDemoControl,
  onRemove,
}: ConditionEditorProps) {
  const { t } = useTranslation();
  const [isOperatorOpen, setOperatorOpen] = useState(false);
  const editorRef = useTourTarget<HTMLDivElement>(
    SEGMENT_TOUR_TARGET.CONDITION_EDITOR,
  );
  const excludeRef = useTourTarget<HTMLDivElement>(SEGMENT_TOUR_TARGET.EXCLUDE);
  const fieldListRef = useTourTarget<HTMLDivElement>(
    SEGMENT_TOUR_TARGET.FIELD_LIST,
  );
  const operatorListRef = useTourTarget<HTMLDivElement>(
    SEGMENT_TOUR_TARGET.OPERATOR_LIST,
  );
  const valueListRef = useTourTarget<HTMLDivElement>(
    SEGMENT_TOUR_TARGET.VALUE_LIST,
  );
  const {
    condition,
    conditionErrors,
    selectedField,
    valueInput,
    handleFieldChange,
    handleInversionChange,
    handleOperatorChange,
    handleValuesChange,
  } = useConditionEditor({ ruleIndex, conditionIndex, fields });

  const optionsFetchers = useFieldOptionsFetcher({
    field: condition?.field ?? "",
    clientId,
    values: condition?.values ?? [],
  });

  const fieldFetcher = useCallback(async () => fields, [fields]);
  const fieldFilterFn = useCallback(
    (option: SegmentField, query: string) =>
      option.label.toLowerCase().includes(query.toLowerCase()),
    [],
  );

  if (!condition) return null;

  const excludeId = `${id}-exclude`;
  const excludeHelpId = `${id}-exclude-help`;

  return (
    <div
      ref={editorRef}
      id={id}
      role="group"
      aria-label={t("admin.segments.builder.editor.label")}
      className="grid gap-4"
    >
      <div
        ref={excludeRef}
        className="flex items-center justify-between rounded-md border px-3 py-2"
      >
        <Label htmlFor={excludeId}>
          {t("admin.segments.builder.editor.exclude")}
        </Label>
        <Switch
          id={excludeId}
          aria-describedby={excludeHelpId}
          className="focus-visible:ring-offset-card"
          checked={condition.isInverted}
          onCheckedChange={handleInversionChange}
        />
        <span id={excludeHelpId} className="sr-only">
          {t("admin.segments.builder.editor.excludeHelp")}
        </span>
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        <div className="grid content-start gap-1">
          <AsyncSelect
            preload
            fetcher={fieldFetcher}
            filterFn={fieldFilterFn}
            getOptionValue={(field) => field.value}
            getDisplayValue={(field) => field.label}
            renderOption={(field) => (
              <span className="truncate">{field.label}</span>
            )}
            value={condition.field}
            onChange={handleFieldChange}
            width="100%"
            clearable={false}
            triggerVariant="filled"
            contentWidth="wide"
            triggerIcon={ChevronDown}
            placeholder={t("placeholders.select", {
              entity: t("common.filter"),
            })}
            aria-invalid={!!conditionErrors?.field}
            aria-describedby={getDescribedBy(
              id,
              "field",
              conditionErrors?.field?.message,
            )}
            label={t("common.filter")}
            forceOpen={tourDemoControl === TOUR_DEMO_CONTROL.FIELD}
            contentRef={fieldListRef}
          />
          <FieldError
            id={getErrorId(id, "field")}
            message={conditionErrors?.field?.message}
          />
        </div>

        {selectedField && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={REVEAL_TRANSITION}
            className="grid content-start gap-1"
          >
            <Select
              open={
                tourDemoControl === TOUR_DEMO_CONTROL.OPERATOR || isOperatorOpen
              }
              onOpenChange={setOperatorOpen}
              value={condition.operator ?? ""}
              onValueChange={handleOperatorChange}
            >
              <SelectTrigger
                variant="filled"
                className="w-full"
                aria-label={t("common.searchType")}
                aria-invalid={!!conditionErrors?.operator}
                aria-describedby={getDescribedBy(
                  id,
                  "operator",
                  conditionErrors?.operator?.message,
                )}
              >
                <SelectValue
                  placeholder={toSentenceCase(
                    t("placeholders.select", {
                      entity: t("common.searchType"),
                    }),
                  )}
                />
              </SelectTrigger>
              <SelectContent ref={operatorListRef}>
                {selectedField.operators.map((operator) => (
                  <SelectItem key={operator} value={operator}>
                    {getOperatorLabel(t, operator, condition.isInverted).text}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FieldError
              id={getErrorId(id, "operator")}
              message={conditionErrors?.operator?.message}
            />
          </motion.div>
        )}

        {valueInput !== VALUE_INPUT.NONE && (
          <motion.div
            key={`${condition.field}-${valueInput}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={REVEAL_TRANSITION}
            className="grid content-start gap-1"
          >
            <ValueInput
              kind={valueInput}
              fieldLabel={selectedField?.label}
              onChange={handleValuesChange}
              values={condition.values}
              forceOpen={tourDemoControl === TOUR_DEMO_CONTROL.VALUE}
              contentRef={valueListRef}
              {...optionsFetchers}
              aria-invalid={!!conditionErrors?.values}
              aria-describedby={getDescribedBy(
                id,
                "value",
                conditionErrors?.values?.message,
              )}
            />
            <FieldError
              id={getErrorId(id, "value")}
              message={conditionErrors?.values?.message}
            />
          </motion.div>
        )}
      </div>

      <div className="flex justify-end">
        <Button
          type="button"
          variant="ghost-destructive"
          size="sm"
          onClick={onRemove}
        >
          {t("common.removeCondition")}
        </Button>
      </div>
    </div>
  );
}
